import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Collapse, Typography, IconButton } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { PlatformService } from '@/services/platformService';
import { useAuthStore } from '@/store/authStore';

export const MaintenanceBanner: React.FC = () => {
  const isSuperAdminFn = useAuthStore((s) => s.isSuperAdmin);
  const isSuperAdmin = Boolean(isSuperAdminFn());
  const [maintenance, setMaintenance] = useState<{ active: boolean; message: string } | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const res = await PlatformService.getSystemStatus();
        if (mounted && res?.data?.maintenanceMode) {
          setMaintenance({
            active: true,
            message: res.data.maintenanceMessage || 'Platform maintenance is currently underway. Some features may be temporarily constrained.',
          });
        }
      } catch {
        // Silently ignore status fetch errors
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 60000); // Re-check every minute
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  if (!maintenance?.active || (isSuperAdmin && dismissed)) {
    return null;
  }

  return (
    <Collapse in={!dismissed}>
      <Box sx={{ width: '100%', mb: 2 }}>
        <Alert
          severity={isSuperAdmin ? 'info' : 'warning'}
          variant="filled"
          icon={<WarningAmberIcon />}
          action={
            isSuperAdmin ? (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setDismissed(true)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            ) : undefined
          }
          sx={{
            borderRadius: 2,
            alignItems: 'center',
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <AlertTitle sx={{ fontWeight: 800, mb: 0.2 }}>
                {isSuperAdmin ? 'Platform Maintenance Active (Super Admin Bypass)' : 'Scheduled System Maintenance in Progress'}
              </AlertTitle>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                {maintenance.message}
              </Typography>
            </Box>
          </Box>
        </Alert>
      </Box>
    </Collapse>
  );
};
